import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Prose } from '@workspace/seedforge-ui/components/prose'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { Input } from '@workspace/ui/components/input'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { Eye } from 'lucide-react'
import { useEffect, useState } from 'react'
import { formatCentsExact } from '../../../features/admin/components/dashboard/dashboard-format'
import { ForfeitBalanceConfirm } from '../../../features/admin/components/forfeit-balance-confirm'
import { HeyReachBlockers } from '../../../features/admin/components/heyreach-blockers'
import {
  type AdminUserMemberships,
  useAdminChangeUserEmailMutation,
  useAdminDeleteUserMutation,
  useAdminDocumentDownloadUrlMutation,
  useAdminUserByIdQuery,
  useAdminUserDeletionPreviewQuery,
  useAdminUserMembershipsQuery,
  useAdminUserTermsAcceptancesQuery,
} from '../../../features/admin/hooks/use-admin-api'
import {
  DocumentPreviewModal,
  type PreviewDocument,
} from '../../../features/preview/components/document-preview-modal'
import { apiErrorMessage } from '../../../lib/api-error'

export const Route = createFileRoute('/admin/users/$id')({
  component: AdminUserDetailPage,
})

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm font-medium">{children}</div>
    </div>
  )
}

function AdminUserDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const userQuery = useAdminUserByIdQuery(id)
  const membershipsQuery = useAdminUserMembershipsQuery(id)
  const termsAcceptancesQuery = useAdminUserTermsAcceptancesQuery(id)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [previewDoc, setPreviewDoc] = useState<PreviewDocument | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const docDownloadMutation = useAdminDocumentDownloadUrlMutation()
  const memberships = (membershipsQuery.data as { data?: AdminUserMemberships } | undefined)?.data

  if (userQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (userQuery.error || !userQuery.data?.data) {
    return (
      <div className="flex items-center justify-center py-20">
        <ErrorBanner message="Failed to load user" />
      </div>
    )
  }

  const user = userQuery.data.data

  return (
    <>
      <div className="space-y-6">
        {/* Avatar + Identity */}
        {user.image && (
          <div className="flex items-center gap-4">
            <img
              src={user.image}
              alt={user.name || 'User avatar'}
              className="size-16 rounded-full border border-border object-cover"
            />
          </div>
        )}

        {/* Core Fields */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard label="Name">{user.name || '\u2014'}</InfoCard>
          <InfoCard label="Email">{user.email}</InfoCard>
          <InfoCard label="Role">
            <Badge variant={user.role === 'ADMIN' ? 'destructive' : 'secondary'} size="sm">
              {user.role}
            </Badge>
          </InfoCard>
          <InfoCard label="User Type">
            {user.userType ? (
              <Badge variant={user.userType === 'FOUNDER' ? 'default' : 'secondary'} size="sm">
                {user.userType}
              </Badge>
            ) : (
              '\u2014'
            )}
          </InfoCard>
          <InfoCard label="Onboarding">
            <Badge variant={user.onboardingCompleted ? 'success' : 'secondary'} size="sm">
              {user.onboardingCompleted ? 'Completed' : 'Pending'}
            </Badge>
          </InfoCard>
          <InfoCard label="Email Subscribed">
            <Badge variant={user.emailSubscribed ? 'success' : 'secondary'} size="sm">
              {user.emailSubscribed ? 'Yes' : 'No'}
            </Badge>
          </InfoCard>
          <InfoCard label="Sign-in">
            <div className="flex flex-wrap gap-1">
              <Badge variant="secondary" size="sm">
                Email code
              </Badge>
              {/* The email code always works regardless of this — it's not
                  an either/or with a linked Google account. */}
              {user.googleLinked && (
                <Badge variant="outline" size="sm">
                  Google
                </Badge>
              )}
            </div>
          </InfoCard>
        </div>

        {/* Profile Fields */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard label="Bio">
            {user.bio ? <Prose className="text-sm">{user.bio}</Prose> : '\u2014'}
          </InfoCard>
          <InfoCard label="Location">{user.location || '\u2014'}</InfoCard>
          {user.websiteUrl && (
            <InfoCard label="Website">
              <a
                href={user.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {user.websiteUrl}
              </a>
            </InfoCard>
          )}
          {user.linkedinUrl && (
            <InfoCard label="LinkedIn">
              <a
                href={user.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {user.linkedinUrl}
              </a>
            </InfoCard>
          )}
          {user.twitterUrl && (
            <InfoCard label="Twitter">
              <a
                href={user.twitterUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {user.twitterUrl}
              </a>
            </InfoCard>
          )}
          {user.githubUrl && (
            <InfoCard label="GitHub">
              <a
                href={user.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {user.githubUrl}
              </a>
            </InfoCard>
          )}
          {user.crunchbaseUrl && (
            <InfoCard label="Crunchbase">
              <a
                href={user.crunchbaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {user.crunchbaseUrl}
              </a>
            </InfoCard>
          )}
        </div>

        {/* Attribution & Terms */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard label="Attribution Source">{user.attributionSource || '\u2014'}</InfoCard>
          <InfoCard label="Attribution Detail">{user.attributionDetail || '\u2014'}</InfoCard>
          <InfoCard label="AI Involvement">
            {[user.attributionAnswer?.aiRole, user.attributionAnswer?.aiTool]
              .filter(Boolean)
              .join(' \u00b7 ') || '\u2014'}
          </InfoCard>
          <InfoCard label="Question Answered At">
            {user.attributionAnsweredAt ? formatDate(user.attributionAnsweredAt) : '\u2014'}
          </InfoCard>
          <InfoCard label="Sign-up Source">
            {user.signupSource
              ? Object.entries(user.signupSource)
                  .map(([field, value]) => `${field}: ${value}`)
                  .join(' \u00b7 ')
              : '\u2014'}
          </InfoCard>
          <InfoCard label="Terms Accepted At">
            {user.termsAcceptedAt ? formatDate(user.termsAcceptedAt) : '\u2014'}
          </InfoCard>
          <InfoCard label="Terms Version">{user.termsVersion || '\u2014'}</InfoCard>
          {user.resumeDocument && (
            <InfoCard label="Resume Document">
              <button
                type="button"
                className="flex items-center gap-1.5 text-primary hover:underline"
                onClick={() => {
                  const doc = user.resumeDocument
                  if (!doc) return
                  setPreviewDoc({
                    id: doc.id,
                    fileName: doc.fileName,
                    mimeType: doc.mimeType ?? null,
                  })
                  setPreviewOpen(true)
                }}
              >
                <Eye className="size-3.5" />
                {user.resumeDocument.fileName}
              </button>
            </InfoCard>
          )}
        </div>

        {/* Terms acceptances, newest first */}
        {termsAcceptancesQuery.data && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Terms acceptances</h3>
            {termsAcceptancesQuery.data.data.length === 0 ? (
              <p className="text-sm text-muted-foreground">No acceptances recorded</p>
            ) : (
              <div className="divide-y divide-border rounded-lg border border-border bg-card">
                {termsAcceptancesQuery.data.data.map((acceptance) => (
                  <div
                    key={acceptance.id}
                    className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
                  >
                    <span className="font-mono text-xs">{acceptance.termsVersion}</span>
                    <span className="text-muted-foreground">
                      {formatDate(acceptance.acceptedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Memberships */}
        {memberships && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Memberships</h3>

            {memberships.startups.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Startups</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {memberships.startups.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
                    >
                      <Link
                        to="/admin/startups/$id"
                        params={{ id: s.id }}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {s.name}
                      </Link>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="secondary" size="sm">
                          {s.role}
                        </Badge>
                        <Badge variant="secondary" size="sm">
                          {s.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {memberships.investors.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Investors</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {memberships.investors.map((inv) => (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
                    >
                      <Link
                        to="/admin/investors/$id"
                        params={{ id: inv.id }}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {inv.name}
                      </Link>
                      <Badge variant="secondary" size="sm">
                        {inv.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {memberships.startups.length === 0 && memberships.investors.length === 0 && (
              <p className="text-sm text-muted-foreground">No memberships</p>
            )}
          </div>
        )}

        {/* Account Actions */}
        <div className="border-t border-border pt-6">
          <div className="rounded-md border border-border bg-card p-4">
            <h3 className="text-sm font-medium">Account Actions</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Change the login email for this user. The new address is used for OTP sign-in, and all
              active sessions are signed out.
            </p>
            <SecondaryButton size="sm" className="mt-3" onClick={() => setEmailDialogOpen(true)}>
              Change login email
            </SecondaryButton>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="border-t border-border pt-6">
          <div className="rounded-md border border-red-500/20 bg-red-500/5 p-4">
            <h3 className="text-sm font-medium text-red-500">Danger Zone</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Permanently delete this user and all associated data.
            </p>
            <SecondaryButton
              tone="danger"
              size="sm"
              className="mt-3"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete user
            </SecondaryButton>
          </div>
        </div>
      </div>

      <DeleteUserDialog
        userId={id}
        userEmail={user.email}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDeleted={() => navigate({ to: '/admin/users' })}
      />
      <ChangeEmailDialog
        userId={id}
        currentEmail={user.email}
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
      />
      <DocumentPreviewModal
        document={previewDoc}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        fetchUrl={async (docId) => {
          const res = await docDownloadMutation.mutateAsync({ path: { id: docId } })
          const data = res as { data?: { downloadUrl?: string } }
          return data.data?.downloadUrl ?? ''
        }}
      />
    </>
  )
}

function DeleteUserDialog({
  userId,
  userEmail,
  open,
  onOpenChange,
  onDeleted,
}: {
  userId: string
  userEmail: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted: () => void
}) {
  const [confirmEmail, setConfirmEmail] = useState('')
  const [deleteStartupIds, setDeleteStartupIds] = useState<string[]>([])
  const [deleteInvestorIds, setDeleteInvestorIds] = useState<string[]>([])
  const [forfeitBalance, setForfeitBalance] = useState(false)

  // Reset on close. Without this the acknowledgement survives cancel-and-reopen,
  // so the next deletion inherits a consent that was never given for it.
  useEffect(() => {
    if (!open) {
      setForfeitBalance(false)
      setConfirmEmail('')
    }
  }, [open])

  // Runs when the dialog opens, so the HeyReach pre-check is not paid for on
  // every page view.
  const previewQuery = useAdminUserDeletionPreviewQuery(userId, open)
  const deleteMutation = useAdminDeleteUserMutation()

  const preview = previewQuery.data?.data

  // Startups selected for deletion that still hold spendable balance. Deleting
  // them destroys that money, so the backend refuses without `forfeitBalance`
  // and the second checkbox below is what supplies it.
  const fundedSelected = (preview?.solelyOwnedStartups ?? []).filter(
    (s) => deleteStartupIds.includes(s.id) && s.walletBalanceCents > 0
  )

  // Only blockers for startups actually selected for deletion should gate the
  // button — an untouched startup's workspace is not this deletion's problem.
  const activeBlockers = (preview?.heyReachBlockers ?? []).filter((b) =>
    deleteStartupIds.includes(b.startupId)
  )

  const canConfirm =
    confirmEmail === userEmail &&
    !deleteMutation.isPending &&
    activeBlockers.length === 0 &&
    (fundedSelected.length === 0 || forfeitBalance)

  const toggleStartup = (id: string) =>
    setDeleteStartupIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    )
  const toggleInvestor = (id: string) =>
    setDeleteInvestorIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    )

  const handleDelete = () => {
    deleteMutation.mutate(
      {
        path: { id: userId },
        body: { deleteStartupIds, deleteInvestorIds, forfeitBalance },
      },
      { onSuccess: () => onDeleted() }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <DialogDescription>
            This action is irreversible. All user data will be permanently deleted.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Deletion preview */}
          {previewQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Loading deletion preview...</p>
          )}
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Documents:</span>{' '}
                  <strong>{preview.documents.length}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Pitch Sessions:</span>{' '}
                  <strong>{preview.pitchSessions.length}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Feedback:</span>{' '}
                  <strong>{preview.feedback}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Automated emails:</span>{' '}
                  <strong>{preview.counts.automatedEmails}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Invites sent:</span>{' '}
                  <strong>{preview.counts.invitesSent}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Blog posts:</span>{' '}
                  <strong>{preview.counts.blogPosts}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Accounts:</span>{' '}
                  <strong>{preview.counts.accounts}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Sessions:</span>{' '}
                  <strong>{preview.counts.sessions}</strong>
                </div>
              </div>

              {/* Solely owned startups */}
              {preview.solelyOwnedStartups.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-red-400">
                    Solely owned startups (select to delete):
                  </p>
                  <div className="space-y-1">
                    {preview.solelyOwnedStartups.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 rounded border border-border px-3 py-2 cursor-pointer hover:bg-muted/50"
                      >
                        <input
                          type="checkbox"
                          checked={deleteStartupIds.includes(s.id)}
                          onChange={() => toggleStartup(s.id)}
                          className="size-3.5"
                        />
                        <span>{s.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {s.documents.length} docs, {s.pitchSessions.length} sessions
                          {s.walletBalanceCents > 0 && (
                            <span className="ml-1.5 font-medium text-amber-500">
                              · {formatCentsExact(s.walletBalanceCents)} balance
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Solely owned investors */}
              {preview.solelyOwnedInvestors.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-red-400">
                    Solely owned investors (select to delete):
                  </p>
                  <div className="space-y-1">
                    {preview.solelyOwnedInvestors.map((inv) => (
                      <label
                        key={inv.id}
                        className="flex items-center gap-2 rounded border border-border px-3 py-2 cursor-pointer hover:bg-muted/50"
                      >
                        <input
                          type="checkbox"
                          checked={deleteInvestorIds.includes(inv.id)}
                          onChange={() => toggleInvestor(inv.id)}
                          className="size-3.5"
                        />
                        <span>{inv.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {inv.personas} personas
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Shared (will be kept) */}
              {(preview.sharedStartups.length > 0 || preview.sharedInvestors.length > 0) && (
                <div className="text-xs text-muted-foreground">
                  Shared memberships (kept):{' '}
                  {[
                    preview.sharedStartups.length > 0 &&
                      `${preview.sharedStartups.length} startups`,
                    preview.sharedInvestors.length > 0 &&
                      `${preview.sharedInvestors.length} investors`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Confirm email */}
          <div className="space-y-2">
            <label htmlFor="confirm-email" className="text-sm">
              Type <span className="font-mono font-medium">{userEmail}</span> to confirm
            </label>
            <Input
              id="confirm-email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={userEmail}
            />
          </div>
          <HeyReachBlockers
            blockers={preview?.heyReachBlockers ?? []}
            selectedStartupIds={deleteStartupIds}
          />
          {fundedSelected.length > 0 && (
            <ForfeitBalanceConfirm
              amountCents={fundedSelected.reduce((sum, s) => sum + s.walletBalanceCents, 0)}
              checked={forfeitBalance}
              onCheckedChange={setForfeitBalance}
              scope={`across ${fundedSelected.length} ${
                fundedSelected.length === 1 ? 'startup' : 'startups'
              }`}
            />
          )}
          {deleteMutation.isError && (
            <p className="text-sm text-red-500">
              {apiErrorMessage(deleteMutation.error, 'Failed to delete user. Please try again.')}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!canConfirm}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ChangeEmailDialog({
  userId,
  currentEmail,
  open,
  onOpenChange,
}: {
  userId: string
  currentEmail: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [newEmail, setNewEmail] = useState('')
  const changeEmailMutation = useAdminChangeUserEmailMutation(userId)

  const trimmed = newEmail.trim()
  const isValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)
  const isUnchanged = trimmed.toLowerCase() === currentEmail.toLowerCase()
  const canSubmit = isValid && !isUnchanged && !changeEmailMutation.isPending

  const handleSubmit = () => {
    changeEmailMutation.mutate(
      { path: { id: userId }, body: { email: trimmed } },
      {
        onSuccess: () => {
          setNewEmail('')
          onOpenChange(false)
        },
      }
    )
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setNewEmail('')
      changeEmailMutation.reset()
    }
    onOpenChange(next)
  }

  const errorMessage =
    (changeEmailMutation.error as { message?: string } | null)?.message ??
    'Failed to change email. Please try again.'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Login Email</DialogTitle>
          <DialogDescription>
            The user will sign in with this address from now on. All active sessions are signed out
            and the new email must be re-verified via OTP on next login.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <InfoCard label="Current Email">{currentEmail}</InfoCard>
          <div className="space-y-2">
            <label htmlFor="new-email" className="text-sm">
              New email
            </label>
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new.address@example.com"
            />
            {isUnchanged && trimmed.length > 0 && (
              <p className="text-sm text-muted-foreground">
                This is already the user's current email.
              </p>
            )}
          </div>
          {changeEmailMutation.isError && <p className="text-sm text-red-500">{errorMessage}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={changeEmailMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {changeEmailMutation.isPending ? 'Saving...' : 'Change Email'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
